/**
 * ===========================================================================
 * LA PIEZA INTERCAMBIABLE
 * ===========================================================================
 *
 * Todo el dictado por voz pasa por `Transcriber`: recibe audio y un idioma, y
 * devuelve texto. Ese es el contrato entero.
 *
 * Hoy detras hay una llamada al API, que a su vez habla con un modelo de
 * transcripcion de OpenAI. Manana puede haber transcripcion en el navegador,
 * otro proveedor o un servicio propio: se escribe otra funcion con esta misma
 * firma y no se toca nada mas. Por eso `fetch` vive AQUI y no en el hook ni en
 * el compositor -- si la llamada estuviera repartida por el componente,
 * cambiar de backend seria perseguirla por media interfaz.
 *
 * El hook (`use-voice-recorder.ts`) recibe el transcriptor como parametro y no
 * sabe si hay red de por medio.
 */
import { send } from "../../lib/api-client.ts";
import { API_BASE } from "../../lib/api-routes.ts";
import type { paths } from "../../lib/api-types.ts";

/**
 * La forma del endpoint NO se escribe a mano: sale del OpenAPI del API por
 * `pnpm api:types`. Si el backend renombra un campo o cambia la respuesta,
 * esto deja de compilar en vez de fallar en tiempo de ejecucion.
 */
type Endpoint = paths[typeof PATH]["post"];
type Field = keyof Endpoint["requestBody"]["content"]["multipart/form-data"];
type Envelope = Endpoint["responses"][200]["content"]["application/json"];

/**
 * Idioma por defecto del dictado. ISO 639-1; el API acepta tambien la forma
 * regional ("es-EC") y se queda con los dos primeros caracteres.
 */
export const DEFAULT_LANGUAGE = "es";

export interface TranscriptionRequest {
  /** El audio grabado, tal cual sale de MediaRecorder. */
  readonly audio: Blob;
  readonly language: string;
  /**
   * Lo que duro la grabacion, medido por el navegador.
   *
   * No es un detalle opcional: el WebM de MediaRecorder se escribe sin
   * duracion en la cabecera, asi que el servidor no la puede deducir. Sin
   * esto, medio segundo de silencio viaja hasta el proveedor y se paga.
   */
  readonly durationMs?: number;
  /**
   * Vocabulario de contexto: nombres propios, marcas, siglas. Maximo 1000
   * caracteres.
   *
   * OJO con que se le mete aqui. No es un diccionario: es contexto que sesga
   * la salida, y con audio corto o silencioso estos modelos devuelven parte
   * del prompt como si lo hubieran oido. Una LISTA DE TERMINOS vale; una
   * frase en curso --lo que el usuario lleva escrito, el ultimo mensaje del
   * chat-- no, porque el eco se lee como texto dictado y nadie sabe de donde
   * salio.
   */
  readonly prompt?: string;
}

export interface TranscriptionResult {
  readonly text: string;
  /** El idioma con el que se transcribio. */
  readonly language: string;
}

export interface TranscriptionOptions {
  /** Para abortar: el usuario puede cancelar mientras se transcribe. */
  readonly signal?: AbortSignal;
}

export type Transcriber = (
  request: TranscriptionRequest,
  options?: TranscriptionOptions,
) => Promise<TranscriptionResult>;

/**
 * Por que un codigo y no solo un mensaje: quien llama tiene que poder
 * distinguir "lo canceló el usuario" --que no es un error y no se enseña-- de
 * los demas casos.
 */
export type TranscriptionErrorCode =
  | "cancelled"
  /** Habia audio, pero no habia voz. */
  | "empty"
  /** El API no acepta ese audio: formato, peso o duracion. */
  | "rejected"
  | "network"
  | "server";

export class TranscriptionError extends Error {
  readonly code: TranscriptionErrorCode;

  constructor(code: TranscriptionErrorCode, message: string) {
    super(message);
    this.name = "TranscriptionError";
    this.code = code;
  }
}

const PATH = `${API_BASE}/transcriptions` as const;

/** Lo que admite el campo `prompt` del API. */
const MAX_PROMPT_LENGTH = 1000;

/**
 * Va por `send` y no por `request`: aqui se necesita el estado de la
 * respuesta, porque un 413 de Nginx no trae nuestro sobre y aun asi hay que
 * decirle algo util al usuario (`failureOf`).
 */
export const transcribeWithApi: Transcriber = async (request, options) => {
  const body = new FormData();
  // Con nombre de archivo, no como Blob suelto: el proveedor decide el
  // decodificador por la extension, y un envio sin nombre se rechaza.
  body.append(
    "audio" satisfies Field,
    request.audio,
    fileNameFor(request.audio.type),
  );
  body.append("language" satisfies Field, request.language);

  if (request.durationMs !== undefined) {
    body.append(
      "durationMs" satisfies Field,
      String(Math.round(request.durationMs)),
    );
  }
  if (request.prompt !== undefined && request.prompt.trim() !== "") {
    body.append(
      "prompt" satisfies Field,
      request.prompt.trim().slice(0, MAX_PROMPT_LENGTH),
    );
  }

  let response: Response;
  try {
    response = await send(PATH, {
      method: "POST",
      body,
      ...(options?.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch {
    throw options?.signal?.aborted === true
      ? new TranscriptionError("cancelled", "Dictado cancelado.")
      : new TranscriptionError(
          "network",
          "No se pudo contactar con el servidor. Comprueba la conexión.",
        );
  }

  if (!response.ok) throw await failureOf(response);

  const payload = (await response
    .json()
    .catch(() => ({}))) as Partial<Envelope>;
  // Se comprueba en vez de darlo por hecho: los tipos salen del OpenAPI, no
  // del cuerpo que llega de verdad.
  const data: Partial<Envelope["data"]> = payload.data ?? {};
  const text = typeof data.text === "string" ? data.text.trim() : "";

  // Un audio sin voz es un 200 con el texto vacío: para el API no es un error,
  // y para el usuario tampoco -- es un dictado que no se oyó.
  if (text === "") {
    throw new TranscriptionError(
      "empty",
      "No se ha entendido nada. Prueba a hablar más cerca del micrófono.",
    );
  }

  return { text, language: data.language ?? request.language };
};

/**
 * Un audio que el API rechaza NO es un fallo del servidor: es algo que el
 * usuario puede arreglar repitiendo el dictado. Los mensajes del API ya vienen
 * escritos para enseñarlos tal cual, así que se respetan.
 */
const failureOf = async (response: Response): Promise<TranscriptionError> => {
  const body = (await response.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string };
  };
  const message = body.error?.message;

  // 413 lo puede dar Nginx ANTES que el API, y entonces ni siquiera llega con
  // nuestro sobre: por eso se mira el estado y no solo el código.
  if (response.status === 413) {
    return new TranscriptionError(
      "rejected",
      message ?? "El audio pesa demasiado. Graba un dictado más corto.",
    );
  }

  // 400 validación (forma, peso) y 422 negocio (formato, duración), §10.4.
  // El 409 se queda: es lo que respondía el API antes del sobre de lala-ms.
  if (
    response.status === 400 ||
    response.status === 409 ||
    response.status === 422
  ) {
    return new TranscriptionError(
      "rejected",
      message ?? "Ese audio no se pudo usar. Inténtalo otra vez.",
    );
  }

  return new TranscriptionError(
    "server",
    message ??
      `No se pudo transcribir el audio (error ${String(response.status)}).`,
  );
};

/** El tipo que da MediaRecorder trae parametros: `audio/webm;codecs=opus`. */
const fileNameFor = (mimeType: string): string => {
  const base = mimeType.split(";")[0]?.trim() ?? "";
  const extension = EXTENSIONS[base] ?? "webm";
  return `dictado.${extension}`;
};

const EXTENSIONS: Readonly<Record<string, string>> = {
  "audio/webm": "webm",
  "audio/mp4": "mp4",
  "audio/m4a": "m4a",
  "audio/x-m4a": "m4a",
  "audio/mpeg": "mp3",
  "audio/mpga": "mp3",
  "audio/mp3": "mp3",
  "audio/wav": "wav",
  "audio/wave": "wav",
  "audio/x-wav": "wav",
};
